import { useState, useMemo, useCallback, useEffect, useContext } from 'react'
import AnalysisEffectCardBody from './AnalysisEffectCardBody'
import AnalysisCardFrame from './AnalysisCardFrame'
import DeepAnalysisEntityFrame from '../deepAnalysis/DeepAnalysisEntityFrame'
import AddToDashboardButton from './AddToDashboardButton'
import DashboardEffectTargetgroupFilter from '../dashboard/DashboardEffectTargetgroupFilter'
import DeepAnalysisButton from '../deepAnalysis/DeepAnalysisButton'
import CardPeriodControl from '../dashboard/CardPeriodControl'
import useEffectTargetgroups from '../../hooks/useEffectTargetgroups'
import resolveComparePeriods from '../../helpers/compareHelpers/resolveComparePeriods'
import DeepAnalysisPeriodContext from '../../StateManagment/DeepAnalysisPeriodContext'
import deepAnalysisPath from '../../helpers/deepAnalysis/deepAnalysisPath'
import { client } from '../../hooks/Client'

// Transient (React-only) default for the period + compare control on Analyse.
const EMPTY_PERIOD = { period: { start: null, end: null }, compare: { enabled: false, start: null, end: null } }

const EMPTY_RESEARCH_IDS = []

/**
 * Analyse-page wrapper around the shared Effect card body. Owns the transient
 * filter + per-indicator option state for analytics mode and renders the
 * targetgroup chip. State is React-only — page reload resets everything.
 *
 * @param {object} props
 * @param {object} props.effect - The Effect doc to render.
 * @param {Array<object>} [props.personas] - Personas list (loaded once at the page level in `Analysis.jsx`).
 * @param {boolean} [props.personasEnabled] - Whether the persona chip should render at all.
 * @param {boolean} [props.showDeepAnalysis] - Show the "Diepte-analyse" action in the card header (Analyse page only — the deep-analysis page itself omits it).
 * @param {boolean} [props.frameless] - Render without the card box (deep-analysis page): no chrome, no pins, details shown inline.
 * @param {React.ComponentType<{children: React.ReactNode}>} [props.frameComponent] - Overrides the frame; the deep-analysis layout passes a passthrough because it provides the slot contexts itself.
 */
const AnalysisEffectCard = ({ effect, personas, personasEnabled, showDeepAnalysis = false, frameless = false, frameComponent }) => {
    const {
        targetgroups,
        researchIdsByTargetgroup,
        multipleQuestionnaires,
        loading: targetgroupsLoading,
    } = useEffectTargetgroups(effect)

    // Local (transient) state. `filterByIndicator` will join here once the
    // per-indicator chip UI lands — for now there's no writer, so keeping
    // empty state would be dead code.
    const [selectedPersonaId, setSelectedPersonaId] = useState('')
    const [selectedTargetgroupId, setSelectedTargetgroupId] = useState('')
    const [graphTypeByIndicator, setGraphTypeByIndicator] = useState({})
    const [colorOverridesByIndicator, setColorOverridesByIndicator] = useState({})
    // Transient period + compare (page reload resets it).
    const [localPeriodValue, setPeriodValue] = useState(EMPTY_PERIOD)
    // On the deep-analysis page the page owns the period (url query) and the
    // card follows it; on 'Analyseren' the card keeps its own.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const pagePeriod = useContext(DeepAnalysisPeriodContext)
    const periodValue = pagePeriod ? (pagePeriod.periodValue || EMPTY_PERIOD) : localPeriodValue
    const { rangeA, rangeB, compareActive } = resolveComparePeriods(periodValue)

    // Reset the targetgroup selection if the chosen group disappears (mirrors
    // dashboard's EffectCardContent — see DashboardVisualisationCard.jsx).
    useEffect(() => {
        if (!selectedTargetgroupId) return
        const stillAvailable = targetgroups.some((t) => t.ID === selectedTargetgroupId)
        if (!stillAvailable) setSelectedTargetgroupId('')
    }, [targetgroups, selectedTargetgroupId])

    const researchIdsOverride = useMemo(
        () => selectedTargetgroupId
            ? (researchIdsByTargetgroup[selectedTargetgroupId] || EMPTY_RESEARCH_IDS)
            : null,
        [selectedTargetgroupId, researchIdsByTargetgroup]
    )

    const targetgroupFilter = !targetgroupsLoading && targetgroups.length > 0 ? (
        <DashboardEffectTargetgroupFilter
            targetgroups={targetgroups}
            selectedTargetgroupId={selectedTargetgroupId}
            onChange={setSelectedTargetgroupId}
            multipleQuestionnaires={multipleQuestionnaires}
        />
    ) : null

    const onClearFilters = useCallback(() => {
        setSelectedTargetgroupId('')
        setSelectedPersonaId('')
    }, [])

    const handleSaveIndicatorOptions = useCallback((patch, indicatorId) => {
        if (!indicatorId) return
        if (Object.prototype.hasOwnProperty.call(patch, 'GraphType')) {
            setGraphTypeByIndicator(prev => ({ ...prev, [indicatorId]: patch.GraphType }))
        }
        if (Object.prototype.hasOwnProperty.call(patch, 'ColorOverrides')) {
            setColorOverridesByIndicator(prev => ({ ...prev, [indicatorId]: patch.ColorOverrides }))
        }
    }, [])

    const getCardSnapshot = useCallback(() => ({
        selectedPersonaId,
        selectedTargetgroupId,
        graphTypeByIndicator,
        colorOverridesByIndicator,
        period: periodValue.period,
        compare: periodValue.compare,
    }), [
        selectedPersonaId,
        selectedTargetgroupId,
        graphTypeByIndicator,
        colorOverridesByIndicator,
        periodValue,
    ])

    const getIndicatorSnapshot = useCallback((indicatorId) => ({
        selectedPersonaId,
        graphType: graphTypeByIndicator[indicatorId],
        colorOverrides: colorOverridesByIndicator[indicatorId],
    }), [selectedPersonaId, graphTypeByIndicator, colorOverridesByIndicator])

    // The pin button stays in the header; the deep-analysis icon portals down
    // into the card's bottom bar (see DeepAnalysisButton — no `inline`).
    const headerAction = showDeepAnalysis ? (
        <span className='analysis-card-header-actions'>
            <CardPeriodControl value={periodValue} onChange={setPeriodValue} />
            <DeepAnalysisButton type='effect' periodValue={periodValue} id={effect.ID} />
            <AddToDashboardButton
                type='effect'
                effectId={effect.ID}
                label={effect.Effect}
                showLabel
                getSnapshot={getCardSnapshot}
            />
        </span>
    ) : undefined

    // The title is a second way into the deep analysis: the icon only has a
    // name on hover (testronde 2026-10-28, B5). Same path builder as the icon.
    // AI-CONTEXT: See docs/features/deep-analysis-period.md
    const titleLink = showDeepAnalysis ? deepAnalysisPath(client, 'effect', effect.ID, periodValue) : undefined

    const Frame = frameComponent || (frameless ? DeepAnalysisEntityFrame : AnalysisCardFrame)

    return (
        <Frame>
            <AnalysisEffectCardBody
                titleLink={titleLink}
                effect={effect}
                headerAction={headerAction}
                hidePins={frameless}
                personas={personas}
                personasEnabled={personasEnabled}
                selectedPersonaId={selectedPersonaId}
                onPersonaChange={setSelectedPersonaId}
                targetgroupFilter={targetgroupFilter}
                researchIdsOverride={researchIdsOverride}
                onClearFilters={onClearFilters}
                showOptions
                graphTypeByIndicator={graphTypeByIndicator}
                colorOverridesByIndicator={colorOverridesByIndicator}
                onSaveIndicatorOptions={handleSaveIndicatorOptions}
                getCardSnapshot={getCardSnapshot}
                getIndicatorSnapshot={getIndicatorSnapshot}
                compareActive={compareActive}
                rangeA={rangeA}
                rangeB={rangeB}
            />
        </Frame>
    )
}

export default AnalysisEffectCard
